/**
 * Service rules and paper types, as plain forms — no code. Everything an agency can change about
 * how papers are served: service hours, holidays, and for each paper type who it can be left with,
 * posting, how many attempts before it can go back non-served, and fees. Publishing makes a new
 * version; older versions are kept.
 */
import { useEffect, useMemo, useState } from 'react';
import { IMMEDIATE_NON_SERVICE_OUTCOMES, OUTCOME_LABELS, UNSUCCESSFUL_OUTCOMES, type AttemptOutcome, type PaperTypeRules, type RuleSetConfig } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { fmtDateTime } from '../shared/format.ts';
import { Badge, Card, ErrorBox, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const CLASSES: [string, string][] = [
  ['attorney', 'Attorneys / law firms'],
  ['magistrate', 'Magistrate courts'],
  ['court', 'Other courts'],
  ['government', 'Government (DSS, etc.)'],
  ['private', 'Private parties'],
];
const DISPOSITIONS = [...IMMEDIATE_NON_SERVICE_OUTCOMES, 'unable_to_locate'] as AttemptOutcome[];
const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));

function YesNo({ label, value, onChange, hint }: { label: string; value: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <div className="re-q">
      <span>
        {label}
        {hint && <span className="small muted"> — {hint}</span>}
      </span>
      <div className="seg-ctl" role="radiogroup" aria-label={label}>
        <button type="button" role="radio" aria-checked={value} className={value ? 'on' : ''} onClick={() => onChange(true)}>
          Yes
        </button>
        <button type="button" role="radio" aria-checked={!value} className={!value ? 'on' : ''} onClick={() => onChange(false)}>
          No
        </button>
      </div>
    </div>
  );
}

function Num({ label, value, onChange, min = 0, max = 999, suffix, hint }: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; suffix?: string; hint?: string }) {
  return (
    <label className="re-num">
      <span>
        {label}
        {hint && <span className="small muted"> — {hint}</span>}
      </span>
      <span className="row gap-sm">
        <input type="number" min={min} max={max} value={Number.isFinite(value) ? value : ''} onChange={(e) => onChange(Number(e.target.value))} aria-label={label} />
        {suffix && <span className="small muted">{suffix}</span>}
      </span>
    </label>
  );
}

export function RulesEditor() {
  const r = useLoad(() => api.get<any>('/api/admin/rules'));
  const affected = useLoad(() => api.get<any>('/api/admin/rules/affected'));
  const a = useAction();
  const [cfg, setCfg] = useState<RuleSetConfig | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [apply, setApply] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [copy, setCopy] = useState({ fromCode: '', newName: '' });
  const [advanced, setAdvanced] = useState(false);
  const [json, setJson] = useState('');
  useEffect(() => {
    if (r.data) {
      setCfg(clone(r.data.active.config));
      setJson(JSON.stringify(r.data.active.config, null, 2));
    }
  }, [r.data]);
  const original = useMemo(() => (r.data ? JSON.stringify(r.data.active.config) : ''), [r.data]);
  if (!r.data || !cfg) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const changed = JSON.stringify(cfg) !== original;
  const up = (f: (c: RuleSetConfig) => void) => setCfg((c) => {
    const n = clone(c!);
    f(n);
    return n;
  });
  const pt = cfg.paperTypes.find((t) => t.code === sel) ?? null;
  const upPt = (f: (t: PaperTypeRules) => void) => up((c) => f(c.paperTypes.find((t) => t.code === sel)!));
  const publish = (config: unknown) =>
    a.run(async () => {
      if (apply && !window.confirm(`Apply the new version to ${affected.data?.openPapers} open paper(s)?`)) return;
      const res = await api.post<any>('/api/admin/rules', { config, notes: notes || 'Changed in the rules editor', applyToOpenPapers: apply, confirmAffectedCount: apply ? affected.data?.openPapers : undefined });
      setDone(`Saved as version ${res.version}. ${res.movedOpenPapers ? `${res.movedOpenPapers} open paper(s) moved to it.` : 'It applies to new papers.'}`);
      setNotes('');
      r.reload();
      affected.reload();
    });
  const fee = (code: string, cls: string) => cfg.fees.find((f) => f.paperTypeCode === code && f.customerClass === cls);
  const setFee = (code: string, cls: string, dollars: string) =>
    up((c) => {
      c.fees = c.fees.filter((f) => !(f.paperTypeCode === code && f.customerClass === cls));
      if (dollars !== '') c.fees.push({ paperTypeCode: code, customerClass: cls as any, amountCents: Math.round(Number(dollars) * 100) });
    });
  return (
    <div className="stack re">
      <Notice tone="warn">These rules decide when a paper can be served, posted or returned non-served. They’re agency settings, not legal advice — check changes with your agency attorney.</Notice>
      {done && <Notice tone="ok">{done}</Notice>}
      <ErrorBox error={a.error} />

      <Card title="When papers can be served">
        <div className="re-grid">
          <label className="re-num">
            <span>Earliest time to serve</span>
            <input type="time" value={cfg.serviceHours.start} onChange={(e) => up((c) => void (c.serviceHours.start = e.target.value))} aria-label="Earliest time to serve" />
          </label>
          <label className="re-num">
            <span>Latest time to serve</span>
            <input type="time" value={cfg.serviceHours.end} onChange={(e) => up((c) => void (c.serviceHours.end = e.target.value))} aria-label="Latest time to serve" />
          </label>
          <YesNo label="Serving on Sunday is blocked" value={cfg.sundayBlocked} onChange={(v) => up((c) => void (c.sundayBlocked = v))} />
          <YesNo label="If a supervisor allows a Sunday/holiday attempt, it counts toward non-service" value={cfg.overrideAttemptsQualify} onChange={(v) => up((c) => void (c.overrideAttemptsQualify = v))} />
          <Num label="Warn the deputy if their GPS is more than" value={cfg.gpsWarningFeet} onChange={(v) => up((c) => void (c.gpsWarningFeet = v))} max={5000} suffix="feet from the address" />
        </div>
      </Card>

      <Card title="Holidays (no service)">
        <ul className="re-list">
          {cfg.holidays.map((h, i) => (
            <li key={i}>
              <input type="date" value={h.date} onChange={(e) => up((c) => void (c.holidays[i]!.date = e.target.value))} aria-label="Holiday date" />
              <input value={h.name} onChange={(e) => up((c) => void (c.holidays[i]!.name = e.target.value))} aria-label="Holiday name" />
              <button type="button" className="btn link small" onClick={() => up((c) => void c.holidays.splice(i, 1))}>
                Remove
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="btn small" onClick={() => up((c) => void c.holidays.push({ date: '', name: '' }))}>
          + Add a holiday
        </button>
      </Card>

      <Card title="Deadlines and reminders">
        <div className="re-grid">
          <YesNo label="Papers that arrive close to the court date are marked priority automatically" value={cfg.urgency.lateReceivedAutoPriority} onChange={(v) => up((c) => void (c.urgency.lateReceivedAutoPriority = v))} />
          <Num label="Alert the supervisor and mark urgent when a paper has no attempts and this many days are left" value={cfg.urgency.escalateAtDaysRemaining} onChange={(v) => up((c) => void (c.urgency.escalateAtDaysRemaining = v))} max={60} suffix="days" />
          <label className="re-num">
            <span>Remind the deputy when this many days are left</span>
            <input value={cfg.urgency.serverReminderDays.join(', ')} onChange={(e) => up((c) => void (c.urgency.serverReminderDays = e.target.value.split(/[,\s]+/).filter(Boolean).map(Number).filter((n) => n >= 0)))} aria-label="Reminder days" placeholder="e.g. 7, 3, 1" />
          </label>
        </div>
      </Card>

      <Card title="Paper types">
        <div className="re-types">
          {cfg.paperTypes.map((t) => (
            <button key={t.code} type="button" className={`re-type${sel === t.code ? ' on' : ''}${t.archived ? ' off' : ''}`} onClick={() => setSel(sel === t.code ? null : t.code)}>
              <strong>{t.name}</strong>
              <span className="small muted">{t.archived ? 'Hidden' : t.substituteAllowed ? 'Can be left with someone' : 'Personal service only'}</span>
            </button>
          ))}
        </div>
        <form
          className="row gap wrap"
          style={{ marginTop: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            a.run(async () => {
              const newCode = copy.newName.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);
              const res = await api.post<any>('/api/admin/paper-types/copy', { fromCode: copy.fromCode, newCode, newName: copy.newName });
              setDone(`Added “${copy.newName}” (version ${res.version}). Click it to set its rules.`);
              setCopy({ fromCode: '', newName: '' });
              setSel(newCode);
              r.reload();
            });
          }}
        >
          <span className="small">New paper type, starting from the rules of</span>
          <select value={copy.fromCode} onChange={(e) => setCopy({ ...copy, fromCode: e.target.value })} required aria-label="Start from">
            <option value="">choose one…</option>
            {cfg.paperTypes.map((t) => (
              <option key={t.code} value={t.code}>
                {t.name}
              </option>
            ))}
          </select>
          <input placeholder="Name of the new paper type" value={copy.newName} onChange={(e) => setCopy({ ...copy, newName: e.target.value })} required aria-label="New paper type name" />
          <button className="btn" disabled={changed} title={changed ? 'Save your other changes first' : undefined}>
            Add
          </button>
        </form>
      </Card>

      {pt && (
        <Card title={`Rules for: ${pt.name}`}>
          <div className="stack">
            <div className="re-grid">
              <label className="re-num">
                <span>Name</span>
                <input value={pt.name} onChange={(e) => upPt((t) => void (t.name = e.target.value))} aria-label="Paper type name" />
              </label>
              <YesNo label="Hide this paper type (no new papers)" value={!!pt.archived} onChange={(v) => upPt((t) => void (t.archived = v))} />
            </div>
            <h4 className="re-h">Who it can be served on</h4>
            <YesNo
              label="Can it be left with someone else at the home (substitute service)?"
              value={pt.substituteAllowed}
              onChange={(v) =>
                upPt((t) => {
                  t.substituteAllowed = v;
                  if (v && !t.substituteRecipient) t.substituteRecipient = { minAge: 18, requireName: true, requireRelationship: true, requireResidesWithDefendant: true, requireCompetent: true, requireNotAParty: true };
                })
              }
            />
            {pt.substituteAllowed && pt.substituteRecipient && (
              <div className="re-sub">
                <Num label="The person must be at least" value={pt.substituteRecipient.minAge} onChange={(v) => upPt((t) => void (t.substituteRecipient!.minAge = v))} max={99} suffix="years old" />
                <YesNo label="Get their name" value={pt.substituteRecipient.requireName} onChange={(v) => upPt((t) => void (t.substituteRecipient!.requireName = v))} />
                <YesNo label="Get how they’re related" value={pt.substituteRecipient.requireRelationship} onChange={(v) => upPt((t) => void (t.substituteRecipient!.requireRelationship = v))} />
                <YesNo label="They must live there" value={pt.substituteRecipient.requireResidesWithDefendant} onChange={(v) => upPt((t) => void (t.substituteRecipient!.requireResidesWithDefendant = v))} />
                <YesNo label="They must be competent" value={pt.substituteRecipient.requireCompetent} onChange={(v) => upPt((t) => void (t.substituteRecipient!.requireCompetent = v))} />
                <YesNo label="They can’t be a party to the case" value={pt.substituteRecipient.requireNotAParty} onChange={(v) => upPt((t) => void (t.substituteRecipient!.requireNotAParty = v))} />
              </div>
            )}
            <label className="re-num">
              <span>Posting it on the door</span>
              <select value={pt.posting} onChange={(e) => upPt((t) => void (t.posting = e.target.value as any))} aria-label="Posting">
                <option value="never">Never allowed</option>
                <option value="after_qualifying_attempts">Allowed after the required attempts</option>
                <option value="any_attempt">Allowed on any attempt</option>
              </select>
            </label>
            <label className="re-num">
              <span>Signature from the person served</span>
              <select value={pt.recipientSignature} onChange={(e) => upPt((t) => void (t.recipientSignature = e.target.value as any))} aria-label="Recipient signature">
                <option value="none">Not asked for</option>
                <option value="optional">Optional</option>
                <option value="required">Required</option>
              </select>
            </label>
            <label className="re-num">
              <span>How it’s finished</span>
              <select value={pt.completion} onChange={(e) => upPt((t) => void (t.completion = e.target.value as any))} aria-label="Completion">
                <option value="standard">Served or non-served (normal)</option>
                <option value="supervisor_action">A supervisor-directed action (writ of assistance)</option>
                <option value="property_documentation">Documenting property (writ of execution)</option>
              </select>
            </label>

            <h4 className="re-h">Returning it non-served</h4>
            <YesNo
              label="Can it go back non-served after enough attempts?"
              value={!!pt.nonService}
              onChange={(v) => upPt((t) => void (t.nonService = v ? { minAttempts: 3, minFullDaysBetween: 1, windows: [], saturdayWildcard: { enabled: false, maxCount: 0 } } : null))}
            />
            {pt.nonService && (
              <div className="re-sub">
                <Num label="Attempts needed" value={pt.nonService.minAttempts} onChange={(v) => upPt((t) => void (t.nonService!.minAttempts = v))} max={20} />
                <Num label="Full days between attempts" value={pt.nonService.minFullDaysBetween} onChange={(v) => upPt((t) => void (t.nonService!.minFullDaysBetween = v))} max={30} />
                <YesNo label="A Saturday attempt can stand in for a time of day" value={pt.nonService.saturdayWildcard.enabled} onChange={(v) => upPt((t) => void (t.nonService!.saturdayWildcard.enabled = v))} />
                {pt.nonService.saturdayWildcard.enabled && <Num label="…at most this many Saturdays" value={pt.nonService.saturdayWildcard.maxCount} onChange={(v) => upPt((t) => void (t.nonService!.saturdayWildcard.maxCount = v))} max={10} />}
                <div>
                  <span>Attempts have to cover these times of day (one attempt in each):</span>
                  <ul className="re-list">
                    {pt.nonService.windows.map((w, i) => (
                      <li key={i}>
                        <input value={w.label} onChange={(e) => upPt((t) => void (t.nonService!.windows[i]!.label = e.target.value))} aria-label="Time of day name" placeholder="Morning" />
                        <input type="time" value={w.start} onChange={(e) => upPt((t) => void (t.nonService!.windows[i]!.start = e.target.value))} aria-label="From" />
                        <span className="small">to</span>
                        <input type="time" value={w.end} onChange={(e) => upPt((t) => void (t.nonService!.windows[i]!.end = e.target.value))} aria-label="To" />
                        <button type="button" className="btn link small" onClick={() => upPt((t) => void t.nonService!.windows.splice(i, 1))}>
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button type="button" className="btn small" onClick={() => upPt((t) => void t.nonService!.windows.push({ id: `w${Date.now()}`, label: '', start: '08:00', end: '12:00' }))}>
                    + Add a time of day
                  </button>
                </div>
              </div>
            )}
            <div>
              <span>These results end it right away as non-served:</span>
              <div className="re-checks">
                {DISPOSITIONS.map((o) => (
                  <label key={o} className="check">
                    <input type="checkbox" checked={pt.immediateDispositions.includes(o)} onChange={(e) => upPt((t) => void (t.immediateDispositions = e.target.checked ? [...t.immediateDispositions, o] : t.immediateDispositions.filter((x) => x !== o)))} /> {OUTCOME_LABELS[o]}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <span>These results go to a supervisor first:</span>
              <div className="re-checks">
                {[...new Set([...DISPOSITIONS, ...UNSUCCESSFUL_OUTCOMES])].map((o) => (
                  <label key={o} className="check">
                    <input type="checkbox" checked={pt.supervisorReviewDispositions.includes(o)} onChange={(e) => upPt((t) => void (t.supervisorReviewDispositions = e.target.checked ? [...t.supervisorReviewDispositions, o] : t.supervisorReviewDispositions.filter((x) => x !== o)))} /> {OUTCOME_LABELS[o]}
                  </label>
                ))}
              </div>
            </div>
            <Num label="Stop serving this many days before the court date" value={pt.serviceCutoffDaysBeforeCourt} onChange={(v) => upPt((t) => void (t.serviceCutoffDaysBeforeCourt = v))} max={60} suffix="days" />

            {pt.eviction && (
              <>
                <h4 className="re-h">Eviction timing</h4>
                <div className="re-sub">
                  <Num label="Hours between the 1st and 2nd attempt" value={pt.eviction.minHoursBetweenAttempt1And2} onChange={(v) => upPt((t) => void (t.eviction!.minHoursBetweenAttempt1And2 = v))} max={240} />
                  <Num label="The 2nd attempt must be at least this many hours different in time of day" value={pt.eviction.minTimeOfDayDifferenceHours} onChange={(v) => upPt((t) => void (t.eviction!.minTimeOfDayDifferenceHours = v))} max={24} />
                  <Num label="Posting allowed this many days after the 2nd attempt" value={pt.eviction.postingMinDaysAfterAttempt2} onChange={(v) => upPt((t) => void (t.eviction!.postingMinDaysAfterAttempt2 = v))} max={30} />
                </div>
              </>
            )}
            {pt.setOut && <Num label="Hours after service before the set-out can be scheduled" value={pt.setOut.waitHoursAfterService} onChange={(v) => upPt((t) => void (t.setOut!.waitHoursAfterService = v))} max={720} />}

            <h4 className="re-h">Service fees</h4>
            <div className="re-fees">
              {CLASSES.map(([k, l]) => (
                <label key={k} className="re-num">
                  <span>{l}</span>
                  <span className="row gap-sm">
                    $
                    <input type="number" min={0} step={0.01} value={fee(pt.code, k) ? fee(pt.code, k)!.amountCents / 100 : ''} onChange={(e) => setFee(pt.code, k, e.target.value)} placeholder="no fee" aria-label={`Fee for ${l}`} />
                  </span>
                </label>
              ))}
            </div>
          </div>
        </Card>
      )}

      <Card title="Save">
        <div className="stack sm">
          {changed ? <Badge tone="warn">You have changes that aren’t saved</Badge> : <span className="small muted">No changes.</span>}
          <label className="re-num">
            <span>What changed (for the record)</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Added Labor Day; raised attorney fee to $25" />
          </label>
          <label className="check">
            <input type="checkbox" checked={apply} onChange={(e) => setApply(e.target.checked)} /> Also use these rules for the {affected.data?.openPapers ?? '…'} paper(s) already open (otherwise only new papers)
          </label>
          <div className="row gap">
            <button className="btn primary" disabled={a.busy || !changed} onClick={() => publish(cfg)}>
              Save changes
            </button>
            <button className="btn" disabled={!changed} onClick={() => setCfg(clone(r.data.active.config))}>
              Undo my changes
            </button>
          </div>
        </div>
      </Card>

      <Card title="Earlier versions">
        <table className="table">
          <thead>
            <tr>
              <th>Version</th>
              <th>What changed</th>
              <th>Open papers on it</th>
              <th>Saved</th>
            </tr>
          </thead>
          <tbody>
            {r.data.versions.map((v: any) => (
              <tr key={v.id}>
                <td>
                  {v.version} {v.id === r.data.active.id && <Badge tone="ok">in use</Badge>}
                </td>
                <td>{v.notes}</td>
                <td>{v.open_papers}</td>
                <td>
                  {fmtDateTime(v.created_at)} {v.created_by_name ? `· ${v.created_by_name}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <details open={advanced} onToggle={(e) => setAdvanced((e.target as HTMLDetailsElement).open)}>
        <summary className="small muted">Advanced: the whole rule set as code (for CivilHound support)</summary>
        <textarea className="mono code" rows={20} value={json} onChange={(e) => setJson(e.target.value)} spellCheck={false} aria-label="Rule set as code" />
        <button
          className="btn"
          disabled={a.busy}
          onClick={() => {
            let c: unknown;
            try {
              c = JSON.parse(json);
            } catch {
              return a.setError(new Error('That isn’t valid — nothing was saved.'));
            }
            publish(c);
          }}
        >
          Save code version
        </button>
      </details>
    </div>
  );
}
